import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Tpl3plService } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';
import { OrderDetailDialogComponent } from '../tpl3pl-export-queue/tpl3pl-export-queue.component';

@Component({
	selector: 'app-tpl3pl-open-orders',
	templateUrl: './tpl3pl-open-orders.component.html',
	styleUrls: ['./tpl3pl-open-orders.component.scss']
})
export class Tpl3plOpenOrdersComponent implements OnInit, AfterViewInit {
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	displayedColumns: string[] = ['pgs_orderno', 'status', 'so', 'po', 'consignee', 'destination', 'warehouse', 'createddate', 'RoutedDate', 'ScheduledPickUpDate'];
	dataSource = new MatTableDataSource<any>([]);
	loading = false;
	rawLoading = false;
	errorMessage: string | null = null;

	// Unified order export activity log.
	activityRows: any[] = [];
	activityTotal = 0;
	activityLoading = false;
	activityError: string | null = null;
	activityFilterStatus: '' | 'success' | 'error' | 'warning' = 'error';
	activityFilterFromDate: string = '';
	activityFilterToDate: string = '';
	activityFilterPo: string = '';
	activityLimit = 100;

	private get thirtyDaysAgo(): string {
		const d = new Date();
		d.setDate(d.getDate() - 30);
		return d.toISOString().slice(0, 10);
	}

	loadActivity(): void {
		this.activityLoading = true;
		this.activityError = null;
		const filters: any = {
			log_type: 'order_export',
			limit: this.activityLimit,
		};
		if (this.activityFilterStatus) { filters.status = this.activityFilterStatus; }
		if (this.activityFilterFromDate) { filters.from_date = this.activityFilterFromDate; }
		if (this.activityFilterToDate) { filters.to_date = this.activityFilterToDate; }
		if ((this.activityFilterPo || '').trim()) { filters.orderno = this.activityFilterPo.trim(); }
		this.tpl3plService.getLogs(filters).subscribe({
			next: (res: any) => {
				this.activityLoading = false;
				this.activityRows = res.data || res || [];
				this.activityTotal = res.total || this.activityRows.length;
			},
			error: () => {
				this.activityLoading = false;
				this.activityError = 'Failed to load order export activity';
			}
		});
	}

	resetActivityFilters(): void {
		this.activityFilterStatus = 'error';
		this.activityFilterFromDate = this.thirtyDaysAgo;
		this.activityFilterToDate = new Date().toISOString().slice(0, 10);
		this.activityFilterPo = '';
		this.loadActivity();
	}

	openActivityAttemptRaw(attempt: any): void {
		const requestPayload = {
			body: attempt?.request_data || null
		};
		const content = {
			request_payload: requestPayload,
			response: attempt?.response_data || null
		};
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				context: 'open_orders',
				content
			},
			panelClass: 'raw-response-dialog'
		});
	}

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.loadOpenOrders();
		this.activityFilterFromDate = this.thirtyDaysAgo;
		this.activityFilterToDate = new Date().toISOString().slice(0, 10);
		this.loadActivity();
	}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
	}

	loadOpenOrders(): void {
		this.loading = true;
		this.errorMessage = null;
		this.tpl3plService.getOpenB2BOrders().subscribe({
			next: (result) => {
				this.loading = false;
				if (result.success && Array.isArray(result.data)) {
					this.dataSource.data = result.data;
				} else {
					this.dataSource.data = [];
					this.errorMessage = (result as any).message || 'No data returned';
				}
			},
			error: (err) => {
				this.loading = false;
				this.dataSource.data = [];
				this.errorMessage = err?.error?.message || err?.message || 'Failed to load open B2B orders';
				this.globalSearchService.showNotification(this.errorMessage, 'danger', 'bottom', 'right');
			}
		});
	}

	getStatusBadgeClass(item: any): string {
		const s = String(item?.status ?? item?.Status ?? item?.OrderStatus ?? '').toLowerCase();
		if (s === 'pending' || s === 'open') return 'badge-warning';
		if (s === 'shipped' || s === 'complete' || s === 'closed') return 'badge-success';
		if (s === 'error' || s === 'failed' || s === 'cancelled') return 'badge-danger';
		return 'badge-secondary';
	}

	formatDate(value: string | number | Date | null | undefined): string {
		if (value == null || value === '') return '-';
		const d = typeof value === 'number' || typeof value === 'string' ? new Date(value) : value;
		return isNaN(d.getTime()) ? '-' : d.toLocaleDateString(undefined, { dateStyle: 'short' });
	}

	/** ERP sales order # from a PGS open-order row (API casing varies). */
	resolveErpOrderNo(row: Record<string, unknown> | null | undefined): string {
		if (!row || typeof row !== 'object') {
			return '';
		}
		const r = row as Record<string, unknown>;
		const candidates = [r.so, r.SO, r.So, r.salesOrder, r.SalesOrder, r.salesorder];
		for (const c of candidates) {
			if (c == null || c === '') {
				continue;
			}
			const s = String(c).trim();
			if (s && s !== '-') {
				return s;
			}
		}
		return '';
	}

	onTableRowClick(row: Record<string, unknown>, ev: MouseEvent): void {
		const el = ev.target as HTMLElement | null;
		if (el?.closest('a,button,input,select,textarea,.mat-sort-header-container')) {
			return;
		}
		const orderno = this.resolveErpOrderNo(row);
		if (!orderno) {
			return;
		}
		this.dialog.open(OrderDetailDialogComponent, {
			data: { orderno },
			width: '90vw',
			maxWidth: '1100px',
			maxHeight: '90vh',
			panelClass: 'order-detail-panel',
		});
	}

	openRawRequestResponse(): void {
		this.rawLoading = true;
		this.tpl3plService.getOpenB2BOrdersRaw().subscribe({
			next: (res) => {
				this.rawLoading = false;
				if (!res.success) {
					this.globalSearchService.showNotification(res.message || 'Could not load raw response', 'warning', 'bottom', 'right');
					return;
				}
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'open_orders',
						content: {
							request_payload: res.request_payload,
							response: res.response
						},
						onSent: () => this.loadOpenOrders()
					},
					panelClass: 'raw-response-dialog'
				});
			},
			error: () => {
				this.rawLoading = false;
				this.globalSearchService.showNotification('Could not load raw request/response', 'danger', 'bottom', 'right');
			}
		});
	}
}
